import { useEffect, useState, useCallback, type FormEvent } from 'react'
import { UserPlus, X, AlertTriangle, KeyRound, Store } from 'lucide-react'
import api, { pesanGalat, bacaSesi } from '../lib/api'

// ═══════════════════════════════════════════════════════════════════════════
//  Pengguna karaoke — daftar tersendiri, bukan pengguna DivX.
//
//  Konsekuensi yang harus terlihat di layar ini, bukan cuma tercatat di
//  komentar migrasi: orang yang sudah keluar dari perusahaan dinonaktifkan di
//  DivX, dan itu TIDAK menutup pintu karaoke. Kolom "nonaktif di DivX" yang
//  membuat kewajiban itu terlihat tanpa harus diingat.
// ═══════════════════════════════════════════════════════════════════════════

type User = {
  id: string; nama: string; email: string; telepon: string | null
  aktif: boolean
  boleh_kelola_katalog: boolean
  boleh_putus_pengadaan: boolean
  boleh_kelola_user: boolean
  terakhir_masuk: string | null
  belum_pernah_masuk: boolean
  nonaktif_di_divx: boolean
  // 28 Okt: akses outlet. Kosong = semua outlet.
  outlet_ids?: string[]
  outlet_nama?: string[]
}

type Outlet = { id: string; nama: string }

const TGL = (s: string | null) =>
  s ? new Date(s).toLocaleDateString('id-ID', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'

export default function Pengguna() {
  const [rows, setRows] = useState<User[]>([])
  const [memuat, setMemuat] = useState(false)
  const [galat, setGalat] = useState('')
  const [formBuka, setFormBuka] = useState(false)
  const [outlets, setOutlets] = useState<Outlet[]>([])
  const [aturOutlet, setAturOutlet] = useState<User | null>(null)
  const sayaID = bacaSesi()?.user.id

  useEffect(() => {
    api.get('/api/outlet').then(r => setOutlets(r.data ?? [])).catch(() => setOutlets([]))
  }, [])

  const muat = useCallback(async () => {
    setMemuat(true); setGalat('')
    try { setRows((await api.get('/api/user')).data ?? []) }
    catch (e) { setGalat(pesanGalat(e, 'Gagal memuat pengguna')) }
    finally { setMemuat(false) }
  }, [])

  useEffect(() => { muat() }, [muat])

  async function ubah(u: User, patch: Record<string, unknown>) {
    setGalat('')
    try { await api.patch(`/api/user/${u.id}`, patch); await muat() }
    catch (e) { setGalat(pesanGalat(e, 'Gagal menyimpan')) }
  }

  async function resetSandi(u: User) {
    const s = prompt(`Kata sandi baru untuk ${u.nama} (minimal 8 karakter):`, '')
    if (s === null) return
    if (s.length < 8) { setGalat('Kata sandi minimal 8 karakter'); return }
    await ubah(u, { password: s })
  }

  const perluDitindak = rows.filter(u => u.aktif && u.nonaktif_di_divx)

  return (
    <div className="space-y-5">
      {/* Peringatan ini adalah harga dari identitas terpisah, ditampilkan
          terus-menerus supaya tidak perlu diingat. */}
      {perluDitindak.length > 0 && (
        <div className="flex items-start gap-2.5 px-4 py-3 rounded-xl bg-amber-500/10
                        border border-amber-500/25 text-amber-400 text-[12.5px] leading-relaxed">
          <AlertTriangle size={15} className="shrink-0 mt-0.5" />
          <div>
            <b>{perluDitindak.length} orang sudah dinonaktifkan di DivX tapi masih aktif di sini.</b>
            <div className="text-amber-400/80 mt-0.5">
              {perluDitindak.map(u => u.nama).join(', ')} — akun karaoke terpisah,
              jadi menonaktifkan di DivX tidak menutup pintu ini.
            </div>
          </div>
        </div>
      )}

      <div className="bg-bg-1 border border-border rounded-xl p-5">
        <div className="flex items-center gap-2.5 mb-4 flex-wrap">
          <span className="text-[12px] text-text-3">{memuat ? 'memuat…' : `${rows.length} pengguna`}</span>
          <div className="flex-1" />
          <button onClick={() => setFormBuka(true)}
            className="flex items-center gap-1.5 text-[12px] px-3 py-1.5 rounded-lg bg-gold/10
                       border border-gold/30 text-gold hover:bg-gold/20 transition-colors">
            <UserPlus size={14} /> Tambah pengguna
          </button>
        </div>

        {galat && (
          <div className="text-[12px] text-red-400 bg-red-500/10 border border-red-500/25 rounded-lg px-3 py-2 mb-3">
            {galat}
          </div>
        )}

        <div className="overflow-x-auto">
          <table className="w-full text-[13px]">
            <thead className="text-[11px] uppercase tracking-wide text-text-3">
              <tr className="border-b border-border">
                <th className="text-left font-medium py-2">Nama</th>
                <th className="text-left font-medium py-2">Email / HP</th>
                <th className="text-center font-medium py-2 w-20">Katalog</th>
                <th className="text-center font-medium py-2 w-24">Pengadaan</th>
                <th className="text-center font-medium py-2 w-20">Pengguna</th>
                <th className="text-left font-medium py-2">Akses outlet</th>
                <th className="text-left font-medium py-2 w-32">Terakhir masuk</th>
                <th className="text-left font-medium py-2 w-40">Aksi</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(u => {
                const saya = u.id === sayaID
                return (
                  <tr key={u.id} className={`border-b border-border/60 hover:bg-bg-2/50 ${!u.aktif ? 'opacity-50' : ''}`}>
                    <td className="py-2.5 pr-3">
                      {u.nama}
                      {saya && <span className="text-[10.5px] text-gold ml-1.5">(Anda)</span>}
                      {!u.aktif && <span className="text-[10.5px] text-red-400 ml-1.5">nonaktif</span>}
                    </td>
                    <td className="py-2.5 pr-3 text-text-2 text-[12px]">
                      {u.email}{u.telepon ? ` · ${u.telepon}` : ''}
                      {u.nonaktif_di_divx && (
                        <span className="block text-[10.5px] text-amber-400">nonaktif di DivX</span>
                      )}
                    </td>
                    <Centang aktif={u.boleh_kelola_katalog}
                      ubah={v => ubah(u, { boleh_kelola_katalog: v })} />
                    <Centang aktif={u.boleh_putus_pengadaan}
                      ubah={v => ubah(u, { boleh_putus_pengadaan: v })} />
                    {/* Izin kelola pengguna pada diri sendiri dikunci: mencabutnya
                        bisa menghasilkan sistem tanpa satu pun orang yang boleh
                        memperbaikinya. Server juga menolaknya. */}
                    <Centang aktif={u.boleh_kelola_user} kunci={saya}
                      ubah={v => ubah(u, { boleh_kelola_user: v })} />
                    <td className="py-2.5 pr-3 text-[12px]">
                      <button onClick={() => setAturOutlet(u)}
                        className="flex items-start gap-1.5 text-left hover:text-gold transition-colors"
                        title="Atur outlet yang boleh dipegang">
                        <Store size={13} className="shrink-0 mt-0.5 text-text-3" />
                        {(u.outlet_ids ?? []).length === 0
                          ? <span className="text-text-3">Semua outlet</span>
                          : <span className="text-text-2">{(u.outlet_nama ?? []).join(', ')}</span>}
                      </button>
                    </td>
                    <td className="py-2.5 text-[12px] text-text-3">
                      {u.belum_pernah_masuk ? 'belum pernah' : TGL(u.terakhir_masuk)}
                    </td>
                    <td className="py-2.5">
                      <div className="flex gap-1.5">
                        <button onClick={() => resetSandi(u)}
                          className="flex items-center gap-1 text-[11px] px-2 py-1 rounded-md bg-bg-3
                                     border border-border text-text-2 hover:border-gold-2 transition-colors">
                          <KeyRound size={12} /> Sandi
                        </button>
                        {!saya && (
                          <button onClick={() => ubah(u, { aktif: !u.aktif })}
                            className={`text-[11px] px-2 py-1 rounded-md border transition-colors ${
                              u.aktif
                                ? 'bg-red-500/10 border-red-500/30 text-red-400 hover:bg-red-500/20'
                                : 'bg-bg-3 border-border text-text-2 hover:border-gold-2'
                            }`}>
                            {u.aktif ? 'Nonaktifkan' : 'Aktifkan'}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                )
              })}
            </tbody>
          </table>
        </div>
      </div>

      {formBuka && <FormUser outlets={outlets} tutup={() => setFormBuka(false)} selesai={() => { setFormBuka(false); muat() }} />}

      {aturOutlet && (
        <ModalOutlet user={aturOutlet} outlets={outlets} tutup={() => setAturOutlet(null)}
          simpan={async (ids) => { await ubah(aturOutlet, { outlet_ids: ids }); setAturOutlet(null) }} />
      )}
    </div>
  )
}

function Centang({ aktif, ubah, kunci }: { aktif: boolean; ubah: (v: boolean) => void; kunci?: boolean }) {
  return (
    <td className="py-2.5 text-center">
      <input type="checkbox" checked={aktif} disabled={kunci}
        onChange={e => ubah(e.target.checked)}
        title={kunci ? 'Tidak bisa mencabut izin ini dari akun sendiri' : undefined}
        className="accent-[#d4af37] disabled:opacity-40 disabled:cursor-not-allowed" />
    </td>
  )
}

// ── PILIH OUTLET (28 Okt) ──────────────────────────────────────────────────
// Tidak ada yang dicentang = SEMUA outlet. Dicentang sebagian = hanya outlet
// itu: daftar room, sesi, antrean, pasang TV/tablet di luar itu ditolak.
function PilihOutlet({ outlets, nilai, set }: { outlets: Outlet[]; nilai: string[]; set: (v: string[]) => void }) {
  return (
    <div className="space-y-1.5">
      <label className="flex items-center gap-2 cursor-pointer select-none text-[12.5px]">
        <input type="checkbox" className="accent-[#d4af37]" checked={nilai.length === 0}
          onChange={() => set([])} />
        <span className="text-text-2">Semua outlet</span>
      </label>
      <div className="grid grid-cols-2 gap-x-3 gap-y-1.5 max-h-56 overflow-y-auto pl-1">
        {outlets.map(o => (
          <label key={o.id} className="flex items-center gap-2 cursor-pointer select-none text-[12.5px]">
            <input type="checkbox" className="accent-[#d4af37]" checked={nilai.includes(o.id)}
              onChange={e => set(e.target.checked ? [...nilai, o.id] : nilai.filter(x => x !== o.id))} />
            <span className="text-text-2">{o.nama}</span>
          </label>
        ))}
      </div>
      <p className="text-[11px] text-text-3 leading-relaxed">
        Kosong = semua outlet. Kalau dipilih, akun ini hanya melihat & mengelola room, sesi, antrean,
        dan TV/tablet di outlet yang dicentang.
      </p>
    </div>
  )
}

function ModalOutlet({ user, outlets, tutup, simpan }: {
  user: User; outlets: Outlet[]; tutup: () => void; simpan: (ids: string[]) => Promise<void>
}) {
  const [ids, setIds] = useState<string[]>(user.outlet_ids ?? [])
  const [sibuk, setSibuk] = useState(false)
  return (
    <div className="fixed inset-0 bg-black/70 grid place-items-center p-5 z-50" onClick={tutup}>
      <div onClick={e => e.stopPropagation()}
        className="w-full max-w-md bg-bg-1 border border-border rounded-2xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Akses outlet — {user.nama}</h2>
          <button type="button" onClick={tutup} className="text-text-3 hover:text-text"><X size={18} /></button>
        </div>
        <PilihOutlet outlets={outlets} nilai={ids} set={setIds} />
        <div className="flex gap-2 justify-end">
          <button type="button" onClick={tutup}
            className="text-[13px] px-4 py-2 rounded-lg bg-bg-3 border border-border text-text-2">Batal</button>
          <button type="button" disabled={sibuk}
            onClick={async () => { setSibuk(true); try { await simpan(ids) } finally { setSibuk(false) } }}
            className="text-[13px] px-4 py-2 rounded-lg bg-gold/15 border border-gold/30 text-gold
                       hover:bg-gold/25 transition-colors disabled:opacity-50">
            {sibuk ? 'Menyimpan…' : 'Simpan'}
          </button>
        </div>
      </div>
    </div>
  )
}

function FormUser({ outlets, tutup, selesai }: { outlets: Outlet[]; tutup: () => void; selesai: () => void }) {
  const [f, setF] = useState({
    nama: '', email: '', telepon: '', password: '',
    boleh_kelola_katalog: false, boleh_putus_pengadaan: false, boleh_kelola_user: false,
    outlet_ids: [] as string[],
  })
  const [galat, setGalat] = useState('')
  const [simpan, setSimpan] = useState(false)

  async function kirim(e: FormEvent) {
    e.preventDefault()
    if (f.password.length < 8) { setGalat('Kata sandi minimal 8 karakter'); return }
    setSimpan(true); setGalat('')
    try { await api.post('/api/user', f); selesai() }
    catch (err) { setGalat(pesanGalat(err, 'Gagal membuat pengguna')) }
    finally { setSimpan(false) }
  }

  const set = (k: string, v: unknown) => setF(p => ({ ...p, [k]: v }))

  return (
    <div className="fixed inset-0 bg-black/70 grid place-items-center p-5 z-50" onClick={tutup}>
      <form onClick={e => e.stopPropagation()} onSubmit={kirim}
        className="w-full max-w-md bg-bg-1 border border-border rounded-2xl p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="font-semibold">Tambah pengguna karaoke</h2>
          <button type="button" onClick={tutup} className="text-text-3 hover:text-text"><X size={18} /></button>
        </div>

        <input required value={f.nama} onChange={e => set('nama', e.target.value)}
          placeholder="Nama *" className="w-full" />
        <input required type="email" value={f.email} onChange={e => set('email', e.target.value)}
          placeholder="Email *" className="w-full" />
        <input value={f.telepon} onChange={e => set('telepon', e.target.value)}
          placeholder="Nomor HP (boleh dipakai untuk masuk)" className="w-full" />
        <input required type="password" value={f.password} onChange={e => set('password', e.target.value)}
          placeholder="Kata sandi awal (min. 8 karakter)" className="w-full" />

        <div className="space-y-2 pt-1">
          <div className="text-[12px] text-text-2">Izin</div>
          {[
            ['boleh_kelola_katalog',  'Kelola katalog', 'Tambah lagu, tandai lisensi, tayangkan/tarik.'],
            ['boleh_putus_pengadaan', 'Putuskan pengadaan', 'Tandai lagu dipesan atau diabaikan di daftar pesanan.'],
            ['boleh_kelola_user',     'Kelola pengguna', 'Tambah dan nonaktifkan pengguna karaoke.'],
          ].map(([k, label, ket]) => (
            <label key={k} className="flex items-start gap-2.5 cursor-pointer select-none">
              <input type="checkbox" className="accent-[#d4af37] mt-0.5"
                checked={f[k as keyof typeof f] as boolean}
                onChange={e => set(k, e.target.checked)} />
              <span>
                <span className="text-[12.5px] text-text-2">{label}</span>
                <span className="block text-[11px] text-text-3 leading-relaxed">{ket}</span>
              </span>
            </label>
          ))}
        </div>

        <div className="space-y-2 pt-1">
          <div className="text-[12px] text-text-2">Akses outlet</div>
          <PilihOutlet outlets={outlets} nilai={f.outlet_ids} set={v => set('outlet_ids', v)} />
        </div>

        {galat && (
          <div className="text-[12px] text-red-400 bg-red-500/10 border border-red-500/25 rounded-lg px-3 py-2">
            {galat}
          </div>
        )}

        <div className="flex gap-2 justify-end">
          <button type="button" onClick={tutup}
            className="text-[13px] px-4 py-2 rounded-lg bg-bg-3 border border-border text-text-2">Batal</button>
          <button type="submit" disabled={simpan}
            className="text-[13px] px-4 py-2 rounded-lg bg-gold/15 border border-gold/30 text-gold
                       hover:bg-gold/25 transition-colors disabled:opacity-50">
            {simpan ? 'Menyimpan…' : 'Buat'}
          </button>
        </div>
      </form>
    </div>
  )
}
